import {
  diffDays,
  isTerminalStatus,
  type CheckResult,
  type CheckSummary,
  type IsoDate,
  type SubmissionStatus,
} from '@clawhire/shared';
import { EMPTY } from '@/lib/format';
import type { ProfileVersion, SubmissionCandidate } from '@/pages/submissions/types';

/** Terminal statuses need an outcome reason; Submitted asks for the sent-on date. */
export function statusChangeNeedsDialog(to: SubmissionStatus): boolean {
  return isTerminalStatus(to) || to === 'SUBMITTED';
}

/** Actual margin is shown with one decimal: 22.2 -> "22.2 %". */
export function formatPct1(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return EMPTY;
  return `${value.toFixed(1)} %`;
}

/**
 * Days a date is overdue (0 = due today). Null when the date is in the future or missing.
 * `inclusive` treats "today" as overdue (follow-ups); otherwise only past dates count (feedback due).
 */
export function overdueDays(
  date: string | null | undefined,
  today: IsoDate,
  inclusive = true,
): number | null {
  if (!date) return null;
  let days: number;
  try {
    days = diffDays(date, today);
  } catch {
    return null;
  }
  if (inclusive ? days >= 0 : days > 0) return days;
  return null;
}

export function overdueText(days: number): string {
  if (days === 0) return 'due today';
  return days === 1 ? '1 day overdue' : `${days} days overdue`;
}

/** "DC-C-2026-0009 · Maria I." style second line for cards and rows. */
export function candidateLine(candidate: SubmissionCandidate): string {
  const name = candidate.fullName?.trim() || candidate.label?.trim();
  return name ? `${candidate.candidateId} · ${name}` : candidate.candidateId;
}

/** "v02" */
export function versionLabel(version: number): string {
  return `v${String(version).padStart(2, '0')}`;
}

/** Tolerates both a CheckSummary and a bare CheckResult[] in `profileVersion.checkResults`. */
export function summarizeCheckResults(
  raw: ProfileVersion['checkResults'],
): { errors: number; warnings: number } | null {
  if (!raw) return null;
  const results: CheckResult[] = Array.isArray(raw)
    ? raw
    : Array.isArray((raw as CheckSummary).results)
      ? (raw as CheckSummary).results
      : [...((raw as CheckSummary).errors ?? []), ...((raw as CheckSummary).warnings ?? [])];
  return {
    errors: results.filter((r) => r.level === 'error').length,
    warnings: results.filter((r) => r.level === 'warning').length,
  };
}
